import React, { useState, useEffect, useMemo } from 'react';
import DatePicker from './DatePicker';
import './SalesActivity.css';
import { formatNumberWithCommas, formatCurrencyInput, formatCurrencyStored, getCurrencyValue } from '../utils/formatters';
import LoadingState from './LoadingState';
import SearchableSelect from './SearchableSelect';
import ConfirmModal from './ConfirmModal';
import { getDocFee, docFeeFor, unitKind } from '../utils/docFee';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// Doc fee carries cents, so show them rather than rounding to whole dollars.
const formatDocFee = (amount) =>
  '$' + Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Currency display that distinguishes "never entered" (null/undefined → blank, so
// the greyed "$0" placeholder shows) from an explicitly entered value (incl. $0).
const displayOrEmpty = (v) => (v === null || v === undefined || v === '') ? '' : formatCurrencyStored(v);

// Amount guardrails (mirror backend backend/src/utils/salesValidation.ts). A
// powersports unit and its F&I products never legitimately reach these, so a
// value above the cap is a typo that would poison every dashboard rollup.
const MAX_UNIT_AMOUNT = 500000;  // hard cap — blocks the save
const ABNORMAL_LOSS = -25000;    // gross this negative on a closed deal → confirm

// Inspect the pricing on a deal. Three tiers, loudest first:
//   `blocking` — save-stopping typos (mirrors the backend 400)
//   `warnings` — implausible gross that we confirm on close
//   `notices`  — an ordinary negative gross; shown inline, never interrupts.
// A unit can legitimately sell at a loss and make it back in F&I, so a notice
// only tells the person entering it what the math came out to.
const getAmountIssues = (data) => {
  const blocking = [];
  const warnings = [];
  const notices = [];
  const price = typeof data.salesPrice === 'number' && !isNaN(data.salesPrice) ? data.salesPrice : null;
  const cost = typeof data.totalCost === 'number' && !isNaN(data.totalCost) ? data.totalCost : null;

  if (price !== null && price > MAX_UNIT_AMOUNT) blocking.push(`Sales price exceeds $${MAX_UNIT_AMOUNT.toLocaleString()} — check for a typo`);
  if (cost !== null && cost > MAX_UNIT_AMOUNT) blocking.push(`Total cost exceeds $${MAX_UNIT_AMOUNT.toLocaleString()} — check for a typo`);
  if (price !== null && price < 0) blocking.push('Sales price cannot be negative');
  if (cost !== null && cost < 0) blocking.push('Total cost cannot be negative');

  if (blocking.length === 0 && price !== null && cost !== null && price > 0) {
    const gross = price - cost;
    if (gross < ABNORMAL_LOSS) {
      warnings.push(`This deal shows a gross loss of ${formatCurrencyStored(gross)} (price ${formatCurrencyStored(price)} − cost ${formatCurrencyStored(cost)}). Double-check the total cost.`);
    } else if (gross < 0) {
      notices.push(`Sales price is below total cost — this deal shows a gross loss of ${formatCurrencyStored(gross)} (price ${formatCurrencyStored(price)} − cost ${formatCurrencyStored(cost)}).`);
    }
  }
  return { blocking, warnings, notices };
};

// Vendor defaults when the dealership hasn't configured its own:
// HDFS for F&I providers, written out as Harley-Davidson Financial Services for the lender.
const HARD_DEFAULT_PROVIDER = 'HDFS';
const HARD_DEFAULT_LENDER = 'Harley-Davidson Financial Services';

// Resolve a dropdown default by priority: dealership-configured → HDFS/Harley match → first item.
const resolveVendorDefault = (configured, list, preferredNames) => {
  if (configured) return configured;
  const match = (list || []).find(o =>
    preferredNames.some(p =>
      String(o.value).toLowerCase() === p.toLowerCase() ||
      String(o.label).toLowerCase() === p.toLowerCase()));
  if (match) return match.value;
  return (list && list[0]?.value) || '';
};

// Financing + F&I product amounts that must be explicitly entered (a typed $0 counts)
// before a deal can be marked Closed/Committed.
// financeManagerId is nullable, so deals reach the list with nobody credited.
// Those are the ones worth hunting for, so the F&I manager filter offers them as
// a bucket of their own rather than leaving them unreachable. Matches the key the
// F&I Scoreboard's leaderboard uses for the same group.
const UNASSIGNED_FI_KEY = '__unassigned__';

const FI_REQUIRED_FIELDS = [
  ['amountFinanced', 'Amount Financed'],
  ['maxCall', 'Max Call'],
  ['vscAmount', 'VSC'],
  ['ppmAmount', 'PPM'],
  ['gapAmount', 'GAP'],
  ['tireAmount', 'Tire'],
  ['theftAmount', 'Theft'],
  ['findItNow', 'Find It Now'],
  ['paintAmount', 'Paint'],
  ['batteryAmount', 'Battery'],
];

function SalesActivity({ user, managedOwner }) {
  const [allActivities, setAllActivities] = useState([]);
  const [dealerships, setDealerships] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [deletingActivity, setDeletingActivity] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  // Filter states
  const [selectedDealerships, setSelectedDealerships] = useState([]);
  const [selectedSalesPeople, setSelectedSalesPeople] = useState([]);
  const [selectedFinanceManagers, setSelectedFinanceManagers] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('saleDate');
  const [sortDirection, setSortDirection] = useState('desc');

  // Bulk import states
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkStep, setBulkStep] = useState('upload');
  const [bulkImportError, setBulkImportError] = useState(null);
  const [bulkImportSuccess, setBulkImportSuccess] = useState(null);
  const [bulkValidation, setBulkValidation] = useState(null);
  const [bulkEntries, setBulkEntries] = useState([]);
  const [bulkFileName, setBulkFileName] = useState('');
  
  const isOwner = user?.role === 'OWNER' || managedOwner;
  const canBulkImport = user?.role === 'OWNER' || user?.role === 'MANAGER' || user?.role === 'ADMIN' || managedOwner;

  useEffect(() => {
    fetchAllData();
  }, [managedOwner]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch dealerships
      let dealershipsEndpoint = isOwner 
        ? `${API_URL}/api/owners/dealerships`
        : `${API_URL}/api/dealerships`;
      
      if (managedOwner && isOwner) {
        dealershipsEndpoint += `?ownerId=${managedOwner.id}`;
      }
        
      const dealershipsRes = await fetch(dealershipsEndpoint, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (dealershipsRes.ok) {
        const dealershipsData = await dealershipsRes.json();
        const fetchedDealerships = dealershipsData.dealerships || [];
        setDealerships(fetchedDealerships);
        
        // Fetch activities for all dealerships
        const activitiesPromises = fetchedDealerships.map(async (d) => {
          const res = await fetch(
            `${API_URL}/api/dealerships/${d.id}/sales-activities`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          if (res.ok) {
            const data = await res.json();
            return data.activities || [];
          }
          return [];
        });
        
        const activitiesArrays = await Promise.all(activitiesPromises);
        const allActivitiesFlat = activitiesArrays.flat();
        setAllActivities(allActivitiesFlat);
        
        // Fetch employees for all dealerships
        const employeesPromises = fetchedDealerships.map(async (d) => {
          const res = await fetch(
            `${API_URL}/api/dealerships/${d.id}/employees`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          if (res.ok) {
            const data = await res.json();
            return (data.employees || []).map(e => ({
              ...e,
              dealershipId: d.id,
              dealershipName: d.name
            }));
          }
          return [];
        });
        
        const employeesArrays = await Promise.all(employeesPromises);
        const allEmployeesFlat = employeesArrays.flat();
        setAllEmployees(allEmployeesFlat);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async (dealershipId) => {
    const employees = allEmployees.filter(e => e.dealershipId === dealershipId);
    return employees;
  };

  const handleNewSale = () => {
    const defaultDealership = dealerships.length > 0 ? dealerships[0].id : '';
    setEditingActivity({
      dealershipId: defaultDealership,
      saleDate: new Date().toISOString().split('T')[0],
      customerFirstName: '',
      customerLastName: '',
      closed: false,
      swrd: false
    });
    setShowForm(true);
  };

  const handleEditActivity = (activity) => {
    setEditingActivity({
      ...activity,
      saleDate: new Date(activity.saleDate).toISOString().split('T')[0]
    });
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingActivity(null);
  };

  const handleDeleteActivity = async () => {
    if (!deletingActivity) return;
    try {
      setDeleteError(null);
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/sales-activities/${deletingActivity.id}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to delete entry');
      }

      // Drop it locally so the table updates without a full refetch.
      setAllActivities(prev => prev.filter(a => a.id !== deletingActivity.id));
      setDeletingActivity(null);
    } catch (error) {
      console.error('Error deleting activity:', error);
      setDeleteError(error.message);
    }
  };

  const getCustomerName = (activity) => {
    const firstName = activity.customerFirstName || '';
    const lastName = activity.customerLastName || '';
    return `${firstName} ${lastName}`.trim() || 'N/A';
  };

  const getStatusBadge = (activity) => {
    if (activity.closed) {
      return <span className="status-badge closed">Closed</span>;
    }
    return <span className="status-badge active">Active</span>;
  };

  const removeFilter = (type, value) => {
    if (type === 'dealership') {
      setSelectedDealerships(prev => prev.filter(d => d !== value));
    } else if (type === 'salesPerson') {
      setSelectedSalesPeople(prev => prev.filter(s => s !== value));
    } else if (type === 'financeManager') {
      setSelectedFinanceManagers(prev => prev.filter(s => s !== value));
    } else if (type === 'status') {
      setSelectedStatuses(prev => prev.filter(s => s !== value));
    }
  };

  const toggleAllDealerships = () => {
    if (selectedDealerships.length === 0) {
      setSelectedDealerships(dealerships.map(d => d.id));
    } else {
      setSelectedDealerships([]);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sales-person options come from the people who actually appear in the entered
  // data (distinct salesPersonName), not every employee account — so the filter
  // lists only relevant names.
  const salesPeopleInData = useMemo(() => {
    const names = new Set();
    allActivities.forEach(a => {
      const name = (a.salesPersonName || '').trim();
      if (name) names.add(name);
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [allActivities]);

  // F&I manager options come from the deals themselves, keyed on financeManagerId
  // rather than on the name. That one key covers all three kinds of manager a deal
  // can carry — a registered user, a name added in the dropdown manager, and a
  // TALON code — without the filter needing to know which it's looking at, since
  // the backend resolves every one of them to financeManagerName on the way out.
  const financeManagersInData = useMemo(() => {
    const byId = new Map();
    let hasUnassigned = false;
    allActivities.forEach(a => {
      if (a.financeManagerId) {
        if (!byId.has(a.financeManagerId)) {
          byId.set(a.financeManagerId, a.financeManagerName || a.financeManagerId);
        }
      } else {
        hasUnassigned = true;
      }
    });
    const list = Array.from(byId.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
    // Unassigned sits at the end: it's a gap to close, not a person to compare.
    if (hasUnassigned) list.push({ id: UNASSIGNED_FI_KEY, name: 'Unassigned' });
    return list;
  }, [allActivities]);

  // Filter activities - wrapped in useMemo to ensure re-render on filter changes
  const filteredActivities = useMemo(() => {
    return allActivities.filter(activity => {
      // Dealership filter
      if (selectedDealerships.length > 0 && !selectedDealerships.includes(activity.dealershipId)) {
        return false;
      }

      // Sales person filter — selections are names (non-TALON) or salesPersonId
      // (TALON enabled); an activity matches if either is selected.
      if (selectedSalesPeople.length > 0 &&
          !selectedSalesPeople.includes((activity.salesPersonName || '').trim()) &&
          !selectedSalesPeople.includes(activity.salesPersonId)) {
        return false;
      }

      // F&I manager filter — selections are financeManagerId values, plus the
      // Unassigned bucket for deals with nobody credited.
      if (selectedFinanceManagers.length > 0) {
        const key = activity.financeManagerId || UNASSIGNED_FI_KEY;
        if (!selectedFinanceManagers.includes(key)) {
          return false;
        }
      }

      // Status filter
      if (selectedStatuses.length > 0) {
        const activityStatus = activity.closed ? 'closed' : 'active';
        if (!selectedStatuses.includes(activityStatus)) {
          return false;
        }
      }

      // Search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const customerName = getCustomerName(activity).toLowerCase();
        const model = (activity.model || '').toLowerCase();
        const salesPerson = (activity.salesPersonName || '').toLowerCase();

        return customerName.includes(search) ||
               model.includes(search) ||
               salesPerson.includes(search);
      }

      return true;
    });
  }, [allActivities, selectedDealerships, selectedSalesPeople, selectedFinanceManagers, selectedStatuses, searchTerm]);

  // Sort activities - wrapped in useMemo to ensure re-render on sort changes
  const sortedActivities = useMemo(() => {
    return [...filteredActivities].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle customer names specially
      if (sortField === 'customerName') {
        aValue = getCustomerName(a);
        bValue = getCustomerName(b);
      }

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      // Handle dates
      if (sortField === 'saleDate') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      // Handle numbers
      if (sortField === 'salePrice' || sortField === 'downPayment') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }

      // Handle strings
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredActivities, sortField, sortDirection]);

  // Calculate active filters
  const activeFilters = [];
  selectedDealerships.forEach(id => {
    const dealership = dealerships.find(d => d.id === id);
    if (dealership) {
      activeFilters.push({ type: 'dealership', value: id, label: dealership.name });
    }
  });
  selectedSalesPeople.forEach(value => {
    // value is a salesPersonId (TALON) or a name (non-TALON); resolve a label for both.
    const activity = allActivities.find(a => a.salesPersonId === value);
    const label = activity?.salesPersonName || allEmployees.find(e => e.id === value)?.name || value;
    activeFilters.push({ type: 'salesPerson', value, label });
  });
  selectedFinanceManagers.forEach(value => {
    const label = financeManagersInData.find(m => m.id === value)?.name || value;
    activeFilters.push({ type: 'financeManager', value, label });
  });
  selectedStatuses.forEach(status => {
    activeFilters.push({
      type: 'status',
      value: status,
      label: status === 'active' ? 'Active' : 'Closed'
    });
  });

  const downloadCSVTemplate = () => {
    const headers = [
      'Date',
      'Dealership Name',
      'Customer First Name',
      'Customer Last Name',
      'Source',
      'Sales Person',
      'ASM',
      'BDC',
      'Sales Manager',
      'Finance Manager',
      'Motorcycle Year',
      'New/Used',
      'Model',
      'H-D Certified',
      'Trade Allowance',
      'Status',
      'ACV',
      'Class',
      'ESP Eligible',
      'Sales Price',
      'Total Cost',
      'Down Payment',
      'Lender',
      'Fee',
      'Rate',
      'Amount Financed',
      'Max Call',
      'VSC',
      'VSC Provider',
      'PPM',
      'GAP',
      'GAP Provider',
      'Tire',
      'T/W Provider',
      'Theft',
      'Theft Provider',
      'Find It Now',
      'Paint',
      'Paint Provider',
      'Battery',
      'Closed',
      'SWRD'
    ];

    const sampleRow = [
      '2025-12-14',
      'Coastal Harley-Davidson',
      'Jane',
      'Doe',
      'Web Lead',
      'Alex Sales',
      'Sam ASM',
      'Taylor BDC',
      'Morgan SM',
      'Finley FM',
      '2025',
      'NEW',
      'Street Glide',
      'Yes',
      'No',
      'Closed',
      '1500',
      'A',
      'Yes',
      '35000',
      '28000',
      '5000',
      'H-D Financial',
      '499',
      '6.5',
      '30000',
      '28000',
      '1800',
      'Zurich',
      '900',
      '800',
      'Allstate',
      '300',
      'Harley T/W',
      '250',
      'Harley Theft',
      '200',
      '400',
      'PPG',
      '150',
      'No',
      'No'
    ];

    const csvContent = [headers.join(','), sampleRow.join(',')].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'fi_sales_template.csv';
    link.click();
  };

  const parseCsvFile = async (file) => {
    const text = await file.text();
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV file must contain headers and at least one data row');
    }

    const csvHeaders = lines[0].split(',').map(h => h.trim());
    const dataLines = lines.slice(1);
    const requiredHeaders = ['Date', 'Dealership Name', 'Sales Price'];
    const missingHeaders = requiredHeaders.filter(h => !csvHeaders.includes(h));
    if (missingHeaders.length > 0) {
      throw new Error(`Missing required headers: ${missingHeaders.join(', ')}`);
    }

    const rows = dataLines.map((line, index) => {
      const values = line.split(',').map(v => v.trim());
      const row = {};
      csvHeaders.forEach((header, i) => {
        row[header] = values[i] || '';
      });

      if (!row['Date'] || !row['Dealership Name'] || !row['Sales Price']) {
        throw new Error(`Row ${index + 2}: Date, Dealership Name, and Sales Price are required`);
      }

      return {
        date: row['Date'],
        dealershipId: row['Dealership Name'],
        customerFirstName: row['Customer First Name'] || '',
        customerLastName: row['Customer Last Name'] || '',
        source: row['Source'] || '',
        salesPerson: row['Sales Person'] || '',
        asm: row['ASM'] || '',
        bdc: row['BDC'] || '',
        salesManager: row['Sales Manager'] || '',
        financeManager: row['Finance Manager'] || '',
        motorcycleYear: row['Motorcycle Year'] || '',
        motorcycleNewUsed: row['New/Used'] || '',
        model: row['Model'] || '',
        hdCertified: row['H-D Certified'] || '',
        // The old 'Trade In' column was yes/no and carries no dollar amount, so
        // it's deliberately not read here — a "Yes" would import as $0 and look
        // like a trade written at book. Old files need the amount filled in.
        tradeAllowance: row['Trade Allowance'] || '0',
        status: row['Status'] || '',
        acv: row['ACV'] || '0',
        dealClass: row['Class'] || '',
        espEligible: row['ESP Eligible'] || '',
        salePrice: row['Sales Price'] || '0',
        totalCost: row['Total Cost'] || '0',
        downPayment: row['Down Payment'] || '0',
        lender: row['Lender'] || '',
        fee: row['Fee'] || '0',
        rate: row['Rate'] || '0',
        amountFinanced: row['Amount Financed'] || '0',
        maxCall: row['Max Call'] || '0',
        vscAmount: row['VSC'] || '0',
        vscProvider: row['VSC Provider'] || '',
        ppmAmount: row['PPM'] || '0',
        gapAmount: row['GAP'] || '0',
        gapProvider: row['GAP Provider'] || '',
        tireAmount: row['Tire'] || '0',
        twProvider: row['T/W Provider'] || '',
        theftAmount: row['Theft'] || '0',
        theftProvider: row['Theft Provider'] || '',
        findItNow: row['Find It Now'] || '0',
        paintAmount: row['Paint'] || '0',
        paintProvider: row['Paint Provider'] || '',
        batteryAmount: row['Battery'] || '0',
        closed: row['Closed'] || 'No',
        swrd: row['SWRD'] || 'No'
      };
    });

    return rows;
  };

  const validateBulkEntries = async (entriesToValidate) => {
    const token = localStorage.getItem('token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch(`${API_URL}/api/sales-activity/bulk-import/validate`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ entries: entriesToValidate })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to validate import');
    }

    return response.json();
  };

  const executeBulkImport = async (entriesToImport) => {
    const token = localStorage.getItem('token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch(`${API_URL}/api/sales-activity/bulk-import/execute`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ entries: entriesToImport })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to import entries');
    }

    return response.json();
  };

  const handleBulkFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setBulkImportError(null);
      setBulkImportSuccess(null);
      setBulkFileName(file.name);

      const parsed = await parseCsvFile(file);
      setBulkEntries(parsed);

      const validation = await validateBulkEntries(parsed);
      setBulkValidation(validation);
      setBulkStep('review');
    } catch (err) {
      setBulkImportError(err.message);
      setBulkValidation(null);
    }

    event.target.value = '';
  };

  const handleBulkExecute = async () => {
    try {
      setBulkImportError(null);
      setBulkImportSuccess(null);
      setBulkStep('importing');

      const entriesToImport = bulkValidation?.normalizedEntries?.length ? bulkValidation.normalizedEntries : bulkEntries;
      const result = await executeBulkImport(entriesToImport);

      setBulkImportSuccess(`Imported ${result.imported} entries`);
      setBulkStep('done');

      await fetchAllData();
    } catch (err) {
      setBulkImportError(err.message);
      setBulkStep('review');
    }
  };

  if (loading) {
    return <LoadingState message="Loading F&I activities..." fullscreen />;
  }

  if (showForm) {
    return (
      <SalesActivityForm
        activity={editingActivity}
        dealershipId={editingActivity?.dealershipId}
        dealerships={dealerships}
        employees={allEmployees}
        user={user}
        managedOwner={managedOwner}
        onClose={handleCloseForm}
        onSave={() => {
          handleCloseForm();
          fetchAllData();
        }}
      />
    );
  }

  return (
    <div className="sales-activity">
      <div className="sales-activity-header">
        <div>
          <h1>F&I Input Logger</h1>
          <p>Track and manage all sales activities</p>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="filter-bar">
        <div className="filter-bar-left">
          <button 
            className={`filter-button ${activeFilters.length > 0 ? 'has-filters' : ''}`}
            onClick={() => setShowFilterPanel(true)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 4h18M3 12h12M3 20h6" strokeLinecap="round"/>
            </svg>
            Filters
            {activeFilters.length > 0 && ` (${activeFilters.length})`}
          </button>

          {activeFilters.length > 0 && (
            <div className="active-filters">
              {activeFilters.map((filter, index) => (
                <div key={index} className="active-filter-chip">
                  {filter.label}
                  <button onClick={() => removeFilter(filter.type, filter.value)}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {canBulkImport && (
          <div className="bulk-import-container">
            <button 
              className="bulk-import-button"
              onClick={() => {
                setShowBulkModal(true);
                setBulkStep('upload');
                setBulkValidation(null);
                setBulkImportError(null);
                setBulkImportSuccess(null);
                setBulkEntries([]);
                setBulkFileName('');
              }}
              title="Bulk Import"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"/>
                <path d="M19.4 15a1.6 1.6 0 0 0 .33 1.8l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.6 1.6 0 0 0-1.8-.33 1.6 1.6 0 0 0-1 1.5V21a2 2 0 0 1-4 0v-.09a1.6 1.6 0 0 0-1-1.5 1.6 1.6 0 0 0-1.8.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.6 1.6 0 0 0 .33-1.8 1.6 1.6 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.09a1.6 1.6 0 0 0 1.5-1 1.6 1.6 0 0 0-.33-1.8l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.6 1.6 0 0 0 1.8.33h.09a1.6 1.6 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.09a1.6 1.6 0 0 0 1 1.5h.09a1.6 1.6 0 0 0 1.8-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.6 1.6 0 0 0-.33 1.8v.09a1.6 1.6 0 0 0 1.5 1H21a2 2 0 0 1 0 4h-.09a1.6 1.6 0 0 0-1.51 1Z"/>
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Filter Panel */}
      {showFilterPanel && (
        <>
          <div className="filter-overlay open" onClick={() => setShowFilterPanel(false)} />
          <div className="filter-panel open">
            <div className="filter-panel-header">
              <h3>Filters</h3>
              <button className="filter-panel-close" onClick={() => setShowFilterPanel(false)}>×</button>
            </div>

            <div className="filter-panel-content">
              {/* Dealership Filter */}
              <div className="filter-section">
                <div className="filter-section-header">
                  <h4>Dealerships</h4>
                  <button onClick={toggleAllDealerships} className="btn-select-all">
                    {selectedDealerships.length === 0 ? 'Select All' : 'Clear All'}
                  </button>
                </div>
                {dealerships.map(dealership => (
                  <label key={dealership.id} className="filter-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedDealerships.includes(dealership.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedDealerships([...selectedDealerships, dealership.id]);
                        } else {
                          setSelectedDealerships(selectedDealerships.filter(d => d !== dealership.id));
                        }
                      }}
                    />
                    <span>{dealership.name} - {dealership.location}</span>
                  </label>
                ))}
              </div>

              {/* Sales Person Filter */}
              <div className="filter-section">
                <div className="filter-section-header">
                  <h4>Sales Person</h4>
                </div>
                {(() => {
                  // With TALON enabled on any dealership, build the list from the
                  // salesPersonId values that appear in activities (TALON-synced
                  // identities). Otherwise list the distinct names present in the
                  // entered data — only people who actually appear, not every account.
                  const anyTalonEnabled = dealerships.some(d => d?.betaFeatures?.talonApi === true);
                  if (anyTalonEnabled) {
                    const seen = new Map();
                    allActivities.forEach(a => {
                      if (a.salesPersonId && !seen.has(a.salesPersonId)) {
                        seen.set(a.salesPersonId, a.salesPersonName || a.salesPersonId);
                      }
                    });
                    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1])).map(([id, name]) => (
                      <label key={id} className="filter-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedSalesPeople.includes(id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedSalesPeople([...selectedSalesPeople, id]);
                            } else {
                              setSelectedSalesPeople(selectedSalesPeople.filter(s => s !== id));
                            }
                          }}
                        />
                        <span>{name}</span>
                      </label>
                    ));
                  }
                  if (salesPeopleInData.length === 0) {
                    return <div className="filter-empty">No sales people in the current data</div>;
                  }
                  return salesPeopleInData.map(name => (
                    <label key={name} className="filter-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedSalesPeople.includes(name)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedSalesPeople([...selectedSalesPeople, name]);
                          } else {
                            setSelectedSalesPeople(selectedSalesPeople.filter(s => s !== name));
                          }
                        }}
                      />
                      <span>{name}</span>
                    </label>
                  ));
                })()}
              </div>

              {/* F&I Manager Filter */}
              <div className="filter-section">
                <div className="filter-section-header">
                  <h4>F&amp;I Manager</h4>
                </div>
                {financeManagersInData.length === 0 ? (
                  <div className="filter-empty">No F&amp;I managers in the current data</div>
                ) : (
                  financeManagersInData.map(({ id, name }) => (
                    <label key={id} className="filter-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedFinanceManagers.includes(id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedFinanceManagers([...selectedFinanceManagers, id]);
                          } else {
                            setSelectedFinanceManagers(selectedFinanceManagers.filter(m => m !== id));
                          }
                        }}
                      />
                      <span>{name}</span>
                    </label>
                  ))
                )}
              </div>

              {/* Status Filter */}
              <div className="filter-section">
                <div className="filter-section-header">
                  <h4>Status</h4>
                </div>
                <label className="filter-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedStatuses.includes('active')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedStatuses([...selectedStatuses, 'active']);
                      } else {
                        setSelectedStatuses(selectedStatuses.filter(s => s !== 'active'));
                      }
                    }}
                  />
                  <span>Active</span>
                </label>
                <label className="filter-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedStatuses.includes('closed')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedStatuses([...selectedStatuses, 'closed']);
                      } else {
                        setSelectedStatuses(selectedStatuses.filter(s => s !== 'closed'));
                      }
                    }}
                  />
                  <span>Closed</span>
                </label>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="activities-list">
        <div className="activities-controls">
          <div className="search-controls">
            <input
              type="text"
              placeholder="Search customer, model, or sales person..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <button className="btn-new-sale" onClick={handleNewSale}>
            + New Sale
          </button>
        </div>

        {sortedActivities.length === 0 ? (
          <div className="no-activities">
            <p>No sales activities found</p>
            {allActivities.length > 0 && <p className="filter-hint">Try adjusting your filters</p>}
            <button onClick={handleNewSale}>Start a new sale</button>
          </div>
        ) : (
          <div className="activities-table-container">
            <table className="activities-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('saleDate')} className="sortable">
                    Date {sortField === 'saleDate' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('customerName')} className="sortable">
                    Customer {sortField === 'customerName' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('model')} className="sortable">
                    Model {sortField === 'model' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('salesPersonName')} className="sortable">
                    Sales Person {sortField === 'salesPersonName' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('salePrice')} className="sortable">
                    Sale Price {sortField === 'salePrice' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('closed')} className="sortable">
                    Status {sortField === 'closed' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedActivities.map(activity => (
                  <tr key={activity.id}>
                    <td>{new Date(activity.saleDate).toLocaleDateString()}</td>
                    <td>{getCustomerName(activity)}</td>
                    <td>{activity.model || 'N/A'}</td>
                    <td>{activity.salesPersonName || 'N/A'}</td>
                    <td>
                      {activity.salesPrice
                        ? formatCurrencyStored(activity.salesPrice)
                        : 'N/A'}
                    </td>
                    <td>{getStatusBadge(activity)}</td>
                    <td>
                      <div className="table-actions">
                        <button
                          className="btn-edit-activity"
                          onClick={() => handleEditActivity(activity)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn-delete-activity"
                          onClick={() => { setDeleteError(null); setDeletingActivity(activity); }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showBulkModal && (
        <div className="bulk-modal-overlay">
          <div className="bulk-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bulk-modal-header">
              <div>
                <h3>Bulk Import (CSV)</h3>
                <p>Steps: Upload → Review → Import</p>
              </div>
              <button className="bulk-modal-close" onClick={() => setShowBulkModal(false)} disabled={bulkStep === 'importing'}>x</button>
            </div>

            <div className="bulk-modal-body">
              {bulkStep === 'upload' && (
                <div className="bulk-step">
                  <p className="bulk-helper">Download the template, add rows with Date, Dealership ID, and Sales Price (required), then upload to validate.</p>
                  <div className="bulk-actions-row">
                    <button className="bulk-secondary" onClick={downloadCSVTemplate}>Download Template</button>
                    <label className="bulk-primary">
                      Attach CSV
                      <input type="file" accept=".csv" onChange={handleBulkFileSelect} style={{ display: 'none' }} />
                    </label>
                  </div>
                  {bulkFileName && <p className="bulk-file-name">Attached: {bulkFileName}</p>}
                  {bulkImportError && <div className="bulk-import-error">{bulkImportError}</div>}
                </div>
              )}

              {bulkStep === 'review' && (
                <div className="bulk-step">
                  <h4>Review</h4>
                  {bulkValidation?.errors?.length ? (
                    <div className="bulk-import-error">
                      <p>Errors found:</p>
                      <ul>
                        {bulkValidation.errors.map((err, idx) => (
                          <li key={idx}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <div className="bulk-import-success">Validation passed. No row errors.</div>
                  )}

                  {bulkImportError && <div className="bulk-import-error">{bulkImportError}</div>}
                  {bulkImportSuccess && <div className="bulk-import-success">{bulkImportSuccess}</div>}
                </div>
              )}

              {bulkStep === 'importing' && (
                <div className="bulk-step">
                  <p>Importing... please wait.</p>
                </div>
              )}

              {bulkStep === 'done' && (
                <div className="bulk-step">
                  <div className="bulk-import-success">Import complete. {bulkImportSuccess}</div>
                  {bulkImportError && <div className="bulk-import-error">{bulkImportError}</div>}
                </div>
              )}
            </div>

            <div className="bulk-modal-footer">
              {bulkStep === 'upload' && (
                <button className="bulk-primary" onClick={downloadCSVTemplate}>Download Template</button>
              )}

              {bulkStep === 'review' && (
                <div className="bulk-footer-actions">
                  <button className="bulk-secondary" onClick={() => setBulkStep('upload')}>Back</button>
                  <button
                    className="bulk-primary"
                    onClick={handleBulkExecute}
                    disabled={!!(bulkValidation?.errors?.length)}
                  >
                    Next: Import
                  </button>
                </div>
              )}

              {bulkStep === 'importing' && (
                <button className="bulk-primary" disabled>Importing...</button>
              )}

              {bulkStep === 'done' && (
                <button className="bulk-primary" onClick={() => setShowBulkModal(false)}>Done</button>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!deletingActivity}
        onClose={() => setDeletingActivity(null)}
        onConfirm={handleDeleteActivity}
        title="Delete F&I Entry"
        message={`Delete the F&I entry for ${deletingActivity ? getCustomerName(deletingActivity) : ''}? This can't be undone.`}
        confirmText="Delete Entry"
        cancelText="Keep Entry"
        confirmStyle="danger"
      />

      <ConfirmModal
        isOpen={!!deleteError}
        onClose={() => setDeleteError(null)}
        onConfirm={() => setDeleteError(null)}
        title="Couldn't Delete Entry"
        message={deleteError || ''}
        confirmText="OK"
        hideCancel
        confirmStyle="primary"
      />
    </div>
  );
}

// Form Component
function SalesActivityForm({ activity, dealershipId, dealerships, employees, user, managedOwner, onClose, onSave }) {
  const [formData, setFormData] = useState(activity || {});
  const [alertModal, setAlertModal] = useState({ isOpen: false, title: '', message: '' });
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
  const [displayValues, setDisplayValues] = useState({
    acv: displayOrEmpty(activity?.acv),
    tradeAllowance: displayOrEmpty(activity?.tradeAllowance),
    salesPrice: displayOrEmpty(activity?.salesPrice),
    totalCost: displayOrEmpty(activity?.totalCost),
    downPayment: displayOrEmpty(activity?.downPayment),
    fee: displayOrEmpty(activity?.fee),
    rate: displayOrEmpty(activity?.rate),
    amountFinanced: displayOrEmpty(activity?.amountFinanced),
    maxCall: displayOrEmpty(activity?.maxCall),
    vscAmount: displayOrEmpty(activity?.vscAmount),
    ppmAmount: displayOrEmpty(activity?.ppmAmount),
    gapAmount: displayOrEmpty(activity?.gapAmount),
    tireAmount: displayOrEmpty(activity?.tireAmount),
    theftAmount: displayOrEmpty(activity?.theftAmount),
    findItNow: displayOrEmpty(activity?.findItNow),
    paintAmount: displayOrEmpty(activity?.paintAmount),
    batteryAmount: displayOrEmpty(activity?.batteryAmount),
    fiPartsAmount: displayOrEmpty(activity?.fiPartsAmount),
    fiServiceAmount: displayOrEmpty(activity?.fiServiceAmount),
    fiApparelAmount: displayOrEmpty(activity?.fiApparelAmount)
  });
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [saveTimeoutId, setSaveTimeoutId] = useState(null);

  // Dropdown options from API
  const [dropdownOptions, setDropdownOptions] = useState({
    lenders: [],
    providers: [],
    motorcycleModels: [],
    sources: [],
    dealClasses: [],
    statuses: []
  });
  const [dropdownDefaults, setDropdownDefaults] = useState({});
  const [dropdownsFetched, setDropdownsFetched] = useState(false);
  const [docFee, setDocFee] = useState(getDocFee(null));
  const [namedFitSpecialists, setNamedFitSpecialists] = useState([]);
  const [namedSalesManagers, setNamedSalesManagers] = useState([]);
  const [namedFinanceManagers, setNamedFinanceManagers] = useState([]);

  // Fallback hardcoded options (used if API fails)
  const fallbackSources = ['PHONE IN', 'PHONE OUT', 'WALK IN', 'NASH', 'CRAIGSLIST', 'CYCLETRADER', 'TRADE CYCLE', 'WEBSITE', 'ONLINE APP', 'REPEAT', 'REFFERAL', 'TEAM MEMBER', 'EVENT', 'INTERNET', 'SERVICE', 'FACEBOOK', 'RIDING ACADEMY', 'RIDER TO RIDER'];
  const fallbackPaymentSources = ['ESB', 'Commerce', 'Independent', 'Veridian', 'Fuel Lease', 'ACA', 'Westlake', 'OSF', 'Cash', 'Launch', 'Fuel Purchase', 'Thunder Road', 'Head Waters', 'Iron Horse'];
  const fallbackProviders = ['HDFS', 'PROTECTIVE', 'GEAR GUARD'];
  const fallbackDealClasses = ['HD', 'STAND ALONE', 'WHOLESALE'];
  const fallbackModels = ['XL', 'Dyna', 'Soft', 'Tour', 'Trike', 'Vrod', 'Other', 'XL1100', 'XL1200', 'XL1200C', 'XL1200CP', 'XL1200N', 'XL1200R', 'XL1200S', 'XL1200T', 'XL1200V', 'XL1200X', 'XL883', 'XL883C', 'XL883L', 'XL883N', 'XL883R'];
  const fallbackStatuses = ['WHOLESALE', 'INVENTORY'];

  // Use API dropdowns if available, otherwise fallback to hardcoded
  const sources = dropdownOptions.sources.length > 0
    ? dropdownOptions.sources
    : fallbackSources.map(s => ({ value: s, label: s }));
  const paymentSources = dropdownOptions.lenders.length > 0
    ? dropdownOptions.lenders
    : fallbackPaymentSources.map(s => ({ value: s, label: s }));
  const providers = dropdownOptions.providers.length > 0
    ? dropdownOptions.providers
    : fallbackProviders.map(s => ({ value: s, label: s }));
  const dealClasses = dropdownOptions.dealClasses.length > 0
    ? dropdownOptions.dealClasses
    : fallbackDealClasses.map(s => ({ value: s, label: s }));
  const models = dropdownOptions.motorcycleModels.length > 0
    ? dropdownOptions.motorcycleModels
    : fallbackModels.map(s => ({ value: s, label: s }));
  const statuses = dropdownOptions.statuses.length > 0
    ? dropdownOptions.statuses
    : fallbackStatuses.map(s => ({ value: s, label: s }));

  // Ensure dealership is set; default to first available
  useEffect(() => {
    if (!formData.dealershipId && dealerships?.length) {
      setFormData(prev => ({ ...prev, dealershipId: dealerships[0].id }));
    }
  }, [dealerships]);

  const effectiveDealershipId = formData.dealershipId || dealerships?.[0]?.id || '';
  const selectedDealership = dealerships?.find(d => d.id === effectiveDealershipId);
  // TALON add-on gate: it only controls the "Other Employees" optgroups, which
  // exist so a TALON-matched employee missing the right role tag can still be
  // picked. Names added under Fit Specialists / F&I Managers / Sales Managers in
  // the dropdown manager are not TALON's — that tab is open to every dealership,
  // so those optgroups render whether or not the add-on is on. Toggled per
  // dealership via betaFeatures.talonApi in the admin panel.
  const talonEnabled = selectedDealership?.betaFeatures?.talonApi === true;
  const singleDealership = (dealerships?.length || 0) === 1;
  const filteredEmployees = employees.filter(e => e.dealershipId === effectiveDealershipId);
  const fitSpecialistEmployees = filteredEmployees.filter(e => Array.isArray(e.roles) && e.roles.includes('Fit Specialist'));
  const asmEmployees = filteredEmployees.filter(e => Array.isArray(e.roles) && e.roles.includes('Assistant Sales Manager'));
  const bdcEmployees = filteredEmployees.filter(e => Array.isArray(e.roles) && e.roles.includes('Business Development Center'));
  const salesManagerEmployees = filteredEmployees.filter(e => Array.isArray(e.roles) && e.roles.includes('Sales Manager'));
  const financeManagerEmployees = filteredEmployees.filter(e => Array.isArray(e.roles) && e.roles.includes('Finance & Insurance Manager'));

  // Fetch dropdown options when dealership changes
  useEffect(() => {
    if (effectiveDealershipId) {
      fetchDropdownOptions(effectiveDealershipId);
    }
  }, [effectiveDealershipId]);

  // Doc fee is configured per dealership under Targets, separately for new and
  // used. It isn't part of the deal record — it's read here only so the amount
  // riding on this bike is visible.
  useEffect(() => {
    if (!effectiveDealershipId) {
      setDocFee(getDocFee(null));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${API_URL}/api/targets/${effectiveDealershipId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          credentials: 'include'
        });
        if (!response.ok) return;
        const data = await response.json();
        if (!cancelled) setDocFee(getDocFee(data.frontEnd || {}));
      } catch (err) {
        // A missing doc fee just means the field stays hidden; nothing to recover.
        console.error('Failed to load doc fee setting:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [effectiveDealershipId]);

  const fetchDropdownOptions = async (dealershipId) => {
    setDropdownsFetched(false);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/dealerships/${dealershipId}/dropdowns`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include'
      });

      if (!response.ok) {
        console.error('Failed to fetch dropdown options, using fallback values');
        setDropdownsFetched(true);
        return;
      }

      const categories = await response.json();

      const formatted = {};
      const defaults = {};

      categories.forEach(category => {
        let options = category.options.map(opt => ({
          value: opt.value,
          label: opt.label,
          groupBy: opt.groupBy,
          familyGroup: opt.familyGroup
        }));

        // Sort motorcycle models by make displayOrder, then family metadata order, then model displayOrder
        if (category.category === 'MOTORCYCLE_MODEL') {
          const makeCategory = categories.find(c => c.category === 'MOTORCYCLE_MAKE');
          const makeOrderMap = {};
          (makeCategory?.options || []).forEach(m => { makeOrderMap[m.value] = m.displayOrder; });
          const familyOrder = (category.metadata && category.metadata.familyOrder) || {};

          options = options.slice().sort((a, b) => {
            const mA = makeOrderMap[a.groupBy] != null ? makeOrderMap[a.groupBy] : 9999;
            const mB = makeOrderMap[b.groupBy] != null ? makeOrderMap[b.groupBy] : 9999;
            if (mA !== mB) return mA - mB;
            const fList = familyOrder[a.groupBy] || [];
            const fA = fList.indexOf(a.familyGroup || 'Uncategorized');
            const fB = fList.indexOf(b.familyGroup || 'Uncategorized');
            const efA = fA === -1 ? 9999 : fA;
            const efB = fB === -1 ? 9999 : fB;
            if (efA !== efB) return efA - efB;
            return a.displayOrder - b.displayOrder;
          });
        }

        switch (category.category) {
          case 'LENDER':
            formatted.lenders = options;
            if (category.defaultValue) defaults.lender = category.defaultValue;
            break;
          case 'FI_PROVIDER':
            formatted.providers = options;
            if (category.defaultValue) defaults.fiProvider = category.defaultValue;
            break;
          case 'MOTORCYCLE_MODEL':
            formatted.motorcycleModels = options;
            if (category.defaultValue) defaults.model = category.defaultValue;
            break;
          case 'SOURCE':
            formatted.sources = options;
            if (category.defaultValue) defaults.source = category.defaultValue;
            break;
          case 'DEAL_CLASS':
            formatted.dealClasses = options;
            if (category.defaultValue) defaults.dealClass = category.defaultValue;
            break;
          case 'STATUS':
            formatted.statuses = options;
            if (category.defaultValue) defaults.status = category.defaultValue;
            break;
          case 'FIT_SPECIALIST':
            setNamedFitSpecialists(category.options.filter(o => o.isActive).map(opt => ({
              value: opt.value,
              label: opt.label
            })));
            break;
          case 'SALES_MANAGER':
            setNamedSalesManagers(category.options.filter(o => o.isActive).map(opt => ({
              value: opt.value,
              label: opt.label
            })));
            break;
          case 'FI_MANAGER':
            setNamedFinanceManagers(category.options.filter(o => o.isActive).map(opt => ({
              value: opt.value,
              label: opt.label
            })));
            break;
          default:
            break;
        }
      });

      // Merge into the full default shape so every key is always an array,
      // even when the API omits a category (e.g. no SOURCE configured for a
      // dealership). Otherwise `dropdownOptions.<key>.length` reads below crash.
      setDropdownOptions({
        lenders: [],
        providers: [],
        motorcycleModels: [],
        sources: [],
        dealClasses: [],
        statuses: [],
        ...formatted,
      });
      setDropdownDefaults(defaults);
      setDropdownsFetched(true);
    } catch (error) {
      console.error('Error fetching dropdown options:', error);
      setDropdownsFetched(true);
    }
  };

  // Apply dropdown defaults for new activities, once the dealership's dropdowns
  // have been fetched (so a configured default wins over the HDFS/first-item fallback).
  useEffect(() => {
    // Only apply defaults for a new activity (no ID), after the fetch resolves.
    if (!activity?.id && dropdownsFetched) {
      // Vendors default to a real configured option (never "pick one"):
      // dealership-configured → HDFS/Harley match → first configured item.
      // IMPORTANT: resolve against the dealership's actual API options only — never the
      // hardcoded fallback list — because the backend rejects any vendor value that
      // isn't a configured dropdown option (which would make every save fail). When the
      // dealership has no configured options, leave the field empty (validation skips it).
      const providerDefault = resolveVendorDefault(dropdownDefaults.fiProvider, dropdownOptions.providers, [HARD_DEFAULT_PROVIDER]);
      const lenderDefault = resolveVendorDefault(dropdownDefaults.lender, dropdownOptions.lenders, [HARD_DEFAULT_LENDER, HARD_DEFAULT_PROVIDER]);
      setFormData(prev => ({
        ...prev,
        lender: prev.lender || lenderDefault,
        vscProvider: prev.vscProvider || providerDefault,
        gapProvider: prev.gapProvider || providerDefault,
        twProvider: prev.twProvider || providerDefault,
        theftProvider: prev.theftProvider || providerDefault,
        paintProvider: prev.paintProvider || providerDefault,
        model: prev.model || dropdownDefaults.model || '',
        source: prev.source || dropdownDefaults.source || '',
        dealClass: prev.dealClass || dropdownDefaults.dealClass || '',
        status: prev.status || dropdownDefaults.status || ''
      }));
    }
  }, [dropdownsFetched, dropdownDefaults, activity?.id]);

  useEffect(() => {
    // Auto-save when required fields are present. This list has to match the guard
    // at the top of handleSave: if it fires in a state the guard rejects, the save
    // is a silent no-op (showMessage is false here) and the edit is lost. Deals
    // predating the F&I manager requirement land in exactly that state, so they
    // hold their edits until a manager is picked and the manual Save says why.
    if (formData.customerFirstName && formData.customerLastName && formData.saleDate && formData.salesPersonId && formData.motorcycleNewUsed && formData.financeManagerId) {
      if (saveTimeoutId) {
        clearTimeout(saveTimeoutId);
      }

      const timeoutId = setTimeout(() => {
        handleSave(false);
      }, 2000); // Auto-save after 2 seconds of no typing

setSaveTimeoutId(timeoutId);
    }

    return () => {
      if (saveTimeoutId) {
        clearTimeout(saveTimeoutId);
      }
    };
  }, [formData]);

  const handleChange = (field, value) => {
    const currencyFields = [
      'acv', 'tradeAllowance', 'salesPrice', 'totalCost', 'downPayment', 'fee', 'rate',
      'amountFinanced', 'maxCall', 'vscAmount', 'ppmAmount', 'gapAmount',
      'tireAmount', 'theftAmount', 'findItNow', 'paintAmount', 'batteryAmount',
      'fiPartsAmount', 'fiServiceAmount', 'fiApparelAmount'
    ];
    
    if (currencyFields.includes(field)) {
      const numValue = value.replace(/[$,]/g, '');
      // Store rounded to cents so entered pennies (e.g. 35,317.40) are preserved
      // without float dust. Empty → null + blank display (greyed "$0" placeholder
      // shows); a typed value (including "0") → numeric + solid display.
      const parsed = numValue === '' ? null : parseFloat(numValue);
      const rounded = (parsed === null || isNaN(parsed)) ? null : Math.round(parsed * 100) / 100;
      setFormData(prev => ({ ...prev, [field]: rounded }));
      setDisplayValues(prev => ({ ...prev, [field]: numValue === '' ? '' : formatCurrencyInput(value) }));
    } else if (field === 'motorcycleNewUsed') {
      // Certified applies to used bikes only; clear it when switching to New.
      setFormData(prev => ({ ...prev, motorcycleNewUsed: value, ...(value === 'NEW' ? { hdCertified: false } : {}) }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  // Live over/under readout under ACV: allowance − ACV, shown as soon as both
  // are filled in. This is the arithmetic of the two fields sitting right next
  // to it, so it's computed directly rather than through overUnderFor — that
  // helper additionally gates on New/Used for the rollups, and a preview that
  // vanished until the bike type was picked would just read as a bug.
  const overUnderPreview = (() => {
    const allowance = Number(formData.tradeAllowance);
    const acv = Number(formData.acv);
    if (!isFinite(allowance) || allowance <= 0) return null;
    if (!isFinite(acv) || acv <= 0) return null;
    return Math.round((allowance - acv) * 100) / 100;
  })();

  // F&I fields that haven't been explicitly entered yet (null/blank). A typed $0 is "entered".
  const getMissingFIFields = (data) => FI_REQUIRED_FIELDS.filter(([k]) => {
    const v = data[k];
    return v === null || v === undefined || v === '' || (typeof v === 'number' && isNaN(v));
  });

  const handleSave = async (showMessage = true) => {
    const activeDealershipId = effectiveDealershipId;

    if (!formData.customerFirstName || !formData.customerLastName || !formData.saleDate || !activeDealershipId || !formData.salesPersonId || !formData.motorcycleNewUsed || !formData.financeManagerId) {
      if (showMessage) {
        setAlertModal({ isOpen: true, title: 'Missing Information', message: 'Dealership, customer name, date, sales person, F&I manager, and new/used are required' });
      }
      return false;
    }

    // Hard stop on out-of-range amounts (mirrors the backend cap). Blocks both the
    // manual save and the 2s auto-save so a fat-fingered value never reaches the DB.
    const { blocking } = getAmountIssues(formData);
    if (blocking.length > 0) {
      if (showMessage) {
        setAlertModal({ isOpen: true, title: 'Check the amounts', message: blocking.join('\n') });
      }
      return false;
    }

    // A deal can only be Closed/Committed once every financing + F&I product field has
    // an explicit value (enter $0 if none). If it's not complete, we still PERSIST the
    // edits (as an open deal, so nothing is lost) but don't apply the closed flag.
    let dataToSave = formData;
    let closeBlocked = false;
    if (formData.closed) {
      const missing = getMissingFIFields(formData);
      if (missing.length > 0) {
        closeBlocked = true;
        dataToSave = { ...formData, closed: false };
        setFormData(prev => ({ ...prev, closed: false }));
        if (showMessage) {
          setAlertModal({
            isOpen: true,
            title: 'Enter F&I values before closing',
            message: `Saved as open. To close, enter a value (use $0 if none) for: ${missing.map(([, label]) => label).join(', ')}`
          });
        }
      }
    }

    try {
      setIsSaving(true);
      const token = localStorage.getItem('token');
      const url = formData.id
        ? `${API_URL}/api/sales-activities/${formData.id}`
        : `${API_URL}/api/dealerships/${activeDealershipId}/sales-activities`;
      
      const method = formData.id ? 'PUT' : 'POST';

      console.log('Saving to URL:', url);
      console.log('Method:', method);
      console.log('Form data:', formData);

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dataToSave)
      });

      console.log('Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        // Never null out formData — the next render reads formData.dealershipId etc.
        if (data?.activity) setFormData(data.activity);
        setLastSaved(new Date());
        // Only the "saved successfully" confirmation here; a blocked close already
        // showed its own "saved as open" message above.
        if (showMessage && !closeBlocked) {
          setAlertModal({ isOpen: true, title: 'Success', message: 'Sales activity saved successfully' });
        }
        // Report success to the caller. A blocked close persisted the edits but did NOT
        // close the deal, so keep the form open for the user to complete F&I.
        return !closeBlocked;
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Save error response:', errorData);
        throw new Error(errorData.error || 'Failed to save');
      }
    } catch (error) {
      console.error('Error saving activity:', error);
      if (showMessage) {
        setAlertModal({ isOpen: true, title: 'Error', message: `Failed to save sales activity: ${error.message}` });
      }
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Only leave the form when the save actually persisted and (if requested) closed.
    if (await handleSave(true)) onSave();
  };

  return (
    <div className="sales-activity-form">
      <div className="form-header">
        <h2>{formData.id ? 'Edit Sale' : 'New Sale'}</h2>
        <div className="form-header-actions">
          {lastSaved && (
            <span className="last-saved">
              Last saved: {lastSaved.toLocaleTimeString()}
            </span>
          )}
          {isSaving && <span className="saving-indicator">Saving...</span>}
          <button 
            type="button" 
            onClick={onClose} 
            className="btn-cancel-header"
          >
            Cancel
          </button>
          <button 
            type="button" 
            onClick={async () => {
              if (await handleSave(true)) onSave();
            }}
            className="btn-close-form"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save & Close'}
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Customer & Sale Info Section */}
        <section className="form-section">
          <h3>Customer & Sale Information</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Dealership</label>
              {singleDealership ? (
                <input
                  type="text"
                  value={selectedDealership?.name || ''}
                  disabled
                  className="readonly-input"
                />
              ) : (
                <select
                  value={effectiveDealershipId}
                  onChange={(e) => handleChange('dealershipId', e.target.value)}
                  required
                >
                  <option value="">Select dealership...</option>
                  {dealerships.map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              )}
            </div>
            <div className="form-group">
              <label>Date</label>
              <DatePicker
                asString
                hideLabel
                className="dp-form"
                selectedDate={formData.saleDate || ''}
                onDateChange={(val) => handleChange('saleDate', val)}
              />
            </div>
            <div className="form-group">
              <label>Customer First Name</label>
              <input
                type="text"
                value={formData.customerFirstName || ''}
                onChange={(e) => handleChange('customerFirstName', e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>Customer Last Name</label>
              <input
                type="text"
                value={formData.customerLastName || ''}
                onChange={(e) => handleChange('customerLastName', e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <SearchableSelect
                options={sources}
                value={formData.source || ''}
                onChange={(value) => handleChange('source', value)}
                label="Source"
                placeholder="Select source..."
              />
            </div>
          </div>
        </section>

        {/* Sales Team Section */}
        <section className="form-section">
          <h3>Sales Team</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Fit Specialist</label>
              {(() => {
                const registeredIds = new Set(fitSpecialistEmployees.map(e => e.id));
                const namedValues = new Set(namedFitSpecialists.map(n => n.value));
                // Any employee matched by TALON but lacking the 'Fit Specialist' role tag
                const otherEmployees = filteredEmployees.filter(
                  e => !registeredIds.has(e.id) && !namedValues.has(e.id)
                );
                return (
                  <select
                    value={formData.salesPersonId || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      const namedSpec = namedFitSpecialists.find(ns => ns.value === val);
                      handleChange('salesPersonId', val);
                      handleChange('salesPersonName', namedSpec ? namedSpec.label : undefined);
                    }}
                    required
                  >
                    <option value="">Select specialist...</option>
                    {fitSpecialistEmployees.length > 0 && (
                      <optgroup label="Fit Specialists">
                        {fitSpecialistEmployees.map(e => (
                          <option key={e.id} value={e.id}>{e.name}</option>
                        ))}
                      </optgroup>
                    )}
                    {namedFitSpecialists.length > 0 && (
                      <optgroup label="Named Specialists">
                        {namedFitSpecialists.map(ns => (
                          <option key={ns.value} value={ns.value}>{ns.label}</option>
                        ))}
                      </optgroup>
                    )}
                    {talonEnabled && otherEmployees.length > 0 && (
                      <optgroup label="Other Employees">
                        {otherEmployees.map(e => (
                          <option key={e.id} value={e.id}>{e.name}</option>
                        ))}
                      </optgroup>
                    )}
                    {fitSpecialistEmployees.length === 0 && namedFitSpecialists.length === 0 && (
                      <option value="" disabled>No fit specialists configured</option>
                    )}
                  </select>
                );
              })()}
            </div>
            <div className="form-group">
              <label>ASM</label>
              <select
                value={formData.asmId || ''}
                onChange={(e) => handleChange('asmId', e.target.value)}
              >
                <option value="">Select ASM...</option>
                {asmEmployees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>BDC</label>
              <select
                value={formData.bdcId || ''}
                onChange={(e) => handleChange('bdcId', e.target.value)}
              >
                <option value="">Select BDC...</option>
                {bdcEmployees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Sales Manager</label>
              {(() => {
                const registeredIds = new Set(salesManagerEmployees.map(e => e.id));
                const namedValues = new Set(namedSalesManagers.map(n => n.value));
                const otherEmployees = filteredEmployees.filter(
                  e => !registeredIds.has(e.id) && !namedValues.has(e.id)
                );
                return (
                  <select
                    value={formData.salesManagerId || ''}
                    onChange={(e) => handleChange('salesManagerId', e.target.value)}
                  >
                    <option value="">Select Sales Manager...</option>
                    {salesManagerEmployees.length > 0 && (
                      <optgroup label="Sales Managers">
                        {salesManagerEmployees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                      </optgroup>
                    )}
                    {namedSalesManagers.length > 0 && (
                      <optgroup label="Named Sales Managers">
                        {namedSalesManagers.map(ns => <option key={ns.value} value={ns.value}>{ns.label}</option>)}
                      </optgroup>
                    )}
                    {talonEnabled && otherEmployees.length > 0 && (
                      <optgroup label="Other Employees">
                        {otherEmployees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                      </optgroup>
                    )}
                    {salesManagerEmployees.length === 0 && namedSalesManagers.length === 0 && (
                      <option value="" disabled>No sales managers configured</option>
                    )}
                  </select>
                );
              })()}
            </div>
            <div className="form-group">
              <label>Finance Manager</label>
              {(() => {
                const registeredIds = new Set(financeManagerEmployees.map(e => e.id));
                const namedValues = new Set(namedFinanceManagers.map(n => n.value));
                const otherEmployees = filteredEmployees.filter(
                  e => !registeredIds.has(e.id) && !namedValues.has(e.id)
                );
                return (
                  <select
                    value={formData.financeManagerId || ''}
                    onChange={(e) => handleChange('financeManagerId', e.target.value)}
                    required
                  >
                    <option value="">Select Finance Manager...</option>
                    {financeManagerEmployees.length > 0 && (
                      <optgroup label="F&I Managers">
                        {financeManagerEmployees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                      </optgroup>
                    )}
                    {namedFinanceManagers.length > 0 && (
                      <optgroup label="Named F&I Managers">
                        {namedFinanceManagers.map(ns => <option key={ns.value} value={ns.value}>{ns.label}</option>)}
                      </optgroup>
                    )}
                    {talonEnabled && otherEmployees.length > 0 && (
                      <optgroup label="Other Employees">
                        {otherEmployees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                      </optgroup>
                    )}
                    {financeManagerEmployees.length === 0 && namedFinanceManagers.length === 0 && (
                      <option value="" disabled>No finance managers configured</option>
                    )}
                  </select>
                );
              })()}
            </div>
          </div>
        </section>

        {/* Motorcycle Details Section */}
        <section className="form-section">
          <h3>Motorcycle Details</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Year</label>
              <input
                type="number"
                value={formData.motorcycleYear || ''}
                onChange={(e) => handleChange('motorcycleYear', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>New/Used</label>
              <select
                value={formData.motorcycleNewUsed || ''}
                onChange={(e) => handleChange('motorcycleNewUsed', e.target.value)}
                required
              >
                <option value="">Select...</option>
                <option value="NEW">New</option>
                <option value="USED">Used</option>
              </select>
            </div>
            <div className="form-group">
              <SearchableSelect
                options={models}
                value={formData.model || ''}
                onChange={(value) => handleChange('model', value)}
                label="Model"
                placeholder="Select model..."
                groupBy="familyGroup"
              />
            </div>
            <div className="form-group">
              <label>H-D Certified</label>
              <select
                value={formData.motorcycleNewUsed === 'NEW' ? 'no' : (formData.hdCertified ? 'yes' : 'no')}
                onChange={(e) => handleChange('hdCertified', e.target.value === 'yes')}
                disabled={formData.motorcycleNewUsed === 'NEW'}
                title={formData.motorcycleNewUsed === 'NEW' ? 'Certified applies to used bikes only' : undefined}
              >
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </select>
            </div>
            <div className="form-group">
              <label>Trade Allowance</label>
              <input
                type="text"
                value={displayValues.tradeAllowance}
                onChange={(e) => handleChange('tradeAllowance', e.target.value)}
                placeholder="$0"
                title="What the customer was given for their trade. Leave blank when there's no trade."
              />
            </div>
            <div className="form-group">
              <label>Status</label>
              <SearchableSelect
                options={statuses}
                value={formData.status || ''}
                onChange={(value) => handleChange('status', value)}             
                placeholder="Select..."
              />
            </div>
            <div className="form-group">
              <label>ACV</label>
              <input
                type="text"
                value={displayValues.acv}
                onChange={(e) => handleChange('acv', e.target.value)}
                placeholder="$0"
              />
              {overUnderPreview !== null && (
                <div
                  style={{
                    marginTop: '6px',
                    fontSize: '12px',
                    lineHeight: 1.35,
                    color: overUnderPreview > 0 ? '#fcd34d' : overUnderPreview < 0 ? '#86efac' : 'rgba(255,255,255,0.6)',
                  }}
                >
                  {overUnderPreview === 0
                    ? 'Trade written at book — no over/under.'
                    : overUnderPreview > 0
                      ? `Over ${formatCurrencyStored(overUnderPreview)} — comes out of this unit's gross.`
                      : `Under ${formatCurrencyStored(Math.abs(overUnderPreview))} — adds to this unit's gross.`}
                </div>
              )}
            </div>
            <div className="form-group">
              <SearchableSelect
                options={dealClasses}
                value={formData.dealClass || ''}
                onChange={(value) => handleChange('dealClass', value)}
                label="Class"
                placeholder="Select..."
              />
            </div>
            <div className="form-group">
              <label>ESP Eligible</label>
              <select
                value={formData.espEligible ? 'yes' : 'no'}
                onChange={(e) => handleChange('espEligible', e.target.value === 'yes')}
              >
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </select>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="form-section">
          <h3>Pricing</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Sales Price (Net Sales)</label>
              <input
                type="text"
                value={displayValues.salesPrice}
                onChange={(e) => handleChange('salesPrice', e.target.value)}
                required
                placeholder="$0"
              />
            </div>
            <div className="form-group">
              <label>Total Cost</label>
              <input
                type="text"
                value={displayValues.totalCost}
                onChange={(e) => handleChange('totalCost', e.target.value)}
                required
                placeholder="$0"
              />
            </div>
            {/* Doc fee is set once per dealership in Targets, not per deal, so it's
                shown read-only here purely so the number on the deal is visible.
                New and used are configured separately, so a deal only shows an
                amount when its own side is switched on. */}
            {docFee.enabled && (() => {
              const amount = docFeeFor(formData, docFee);
              // Which sides the dealership charges on, for the "why is this blank?"
              // hint on a deal that doesn't carry one.
              const appliesTo = docFee.new.enabled && docFee.used.enabled
                ? 'new and used bikes'
                : docFee.new.enabled ? 'new bikes only' : 'used bikes only';
              return (
                <div className="form-group">
                  <label>Doc Fee</label>
                  <input
                    type="text"
                    value={amount > 0 ? formatDocFee(amount) : '—'}
                    readOnly
                    disabled
                    title={amount > 0
                      ? 'Set in Targets and counted as back-end (F&I) gross. Not entered per deal.'
                      : `Doc fee applies to ${appliesTo}.`}
                  />
                  <span className="field-hint">
                    {amount > 0
                      ? 'From Targets—counts toward back-end gross'
                      : unitKind(formData)
                        ? `Applies to ${appliesTo}`
                        : 'Choose New or Used to see the fee'}
                  </span>
                </div>
              );
            })()}
          </div>
          {(() => {
            const { blocking, warnings, notices } = getAmountIssues(formData);
            const messages = [...blocking, ...warnings, ...notices];
            if (messages.length === 0) return null;
            const isBlocking = blocking.length > 0;
            return (
              <div
                role="alert"
                style={{
                  marginTop: '12px',
                  padding: '10px 14px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  lineHeight: 1.4,
                  border: `1px solid ${isBlocking ? 'rgba(239,68,68,0.5)' : 'rgba(245,158,11,0.5)'}`,
                  background: isBlocking ? 'rgba(239,68,68,0.12)' : 'rgba(245,158,11,0.12)',
                  color: isBlocking ? '#fca5a5' : '#fcd34d',
                }}
              >
                {messages.map((m, i) => (
                  <div key={i}>⚠ {m}</div>
                ))}
              </div>
            );
          })()}
        </section>

        {/* Financing Section */}
        <section className="form-section">
          <h3>Financing</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Down Payment</label>
              <input
                type="text"
                value={displayValues.downPayment}
                onChange={(e) => handleChange('downPayment', e.target.value)}
                placeholder="$0"
              />
            </div>
            <div className="form-group">
              <SearchableSelect
                options={paymentSources}
                value={formData.lender || ''}
                onChange={(value) => handleChange('lender', value)}
                label="Lender"
                placeholder="Select lender..."
              />
            </div>
            <div className="form-group">
              <label>Fee</label>
              <input
                type="text"
                value={displayValues.fee}
                onChange={(e) => handleChange('fee', e.target.value)}
                placeholder="$0"
              />
            </div>
            <div className="form-group">
              <label>Rate ($)</label>
              <input
                type="text"
                value={displayValues.rate}
                onChange={(e) => handleChange('rate', e.target.value)}
                placeholder="$0"
              />
            </div>
            <div className="form-group">
              <label>Amount Financed</label>
              <input
                type="text"
                value={displayValues.amountFinanced}
                onChange={(e) => handleChange('amountFinanced', e.target.value)}
                placeholder="$0"
              />
            </div>
            <div className="form-group">
              <label>Max Call</label>
              <input
                type="text"
                value={displayValues.maxCall}
                onChange={(e) => handleChange('maxCall', e.target.value)}
                placeholder="$0"
              />
            </div>
          </div>
        </section>

        {/* F&I Products Section */}
        <section className="form-section">
          <h3>F&I Products</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>VSC</label>
              <input
                type="text"
                value={displayValues.vscAmount}
                onChange={(e) => handleChange('vscAmount', e.target.value)}
                placeholder="$0"
              />
            </div>
            <div className="form-group">
              <SearchableSelect
                options={providers}
                value={formData.vscProvider || ''}
                onChange={(value) => handleChange('vscProvider', value)}
                label="VSC Provider"
                placeholder="Select provider..."
              />
            </div>
            <div className="form-group">
              <label>PPM</label>
              <input
                type="text"
                value={displayValues.ppmAmount}
                onChange={(e) => handleChange('ppmAmount', e.target.value)}
                placeholder="$0"
              />
            </div>
            <div className="form-group">
              <label>GAP</label>
              <input
                type="text"
                value={displayValues.gapAmount}
                onChange={(e) => handleChange('gapAmount', e.target.value)}
                placeholder="$0"
              />
            </div>
            <div className="form-group">
              <SearchableSelect
                options={providers}
                value={formData.gapProvider || ''}
                onChange={(value) => handleChange('gapProvider', value)}
                label="GAP Provider"
                placeholder="Select provider..."
              />
            </div>
          </div>
        </section>

        {/* Additional Products Section */}
        <section className="form-section">
          <h3>Additional Products</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Tire</label>
              <input
                type="text"
                value={displayValues.tireAmount}
                onChange={(e) => handleChange('tireAmount', e.target.value)}
                placeholder="$0"
              />
            </div>
            <div className="form-group">
              <SearchableSelect
                options={providers}
                value={formData.twProvider || ''}
                onChange={(value) => handleChange('twProvider', value)}
                label="T/W Provider"
                placeholder="Select provider..."
              />
            </div>
            <div className="form-group">
              <label>Theft</label>
              <input
                type="text"
                value={displayValues.theftAmount}
                onChange={(e) => handleChange('theftAmount', e.target.value)}
                placeholder="$0"
              />
            </div>
            <div className="form-group">
              <SearchableSelect
                options={providers}
                value={formData.theftProvider || ''}
                onChange={(value) => handleChange('theftProvider', value)}
                label="Theft Provider"
                placeholder="Select provider..."
              />
            </div>
            <div className="form-group">
              <label>Find It Now</label>
              <input
                type="text"
                value={displayValues.findItNow}
                onChange={(e) => handleChange('findItNow', e.target.value)}
                placeholder="$0"
              />
            </div>
            <div className="form-group">
              <label>Paint</label>
              <input
                type="text"
                value={displayValues.paintAmount}
                onChange={(e) => handleChange('paintAmount', e.target.value)}
                placeholder="$0"
              />
            </div>
            <div className="form-group">
              <SearchableSelect
                options={providers}
                value={formData.paintProvider || ''}
                onChange={(value) => handleChange('paintProvider', value)}
                label="Paint Provider"
                placeholder="Select provider..."
              />
            </div>
            <div className="form-group">
              <label>Battery</label>
              <input
                type="text"
                value={displayValues.batteryAmount}
                onChange={(e) => handleChange('batteryAmount', e.target.value)}
                placeholder="$0"
              />
            </div>
          </div>
        </section>

        {/* Fixed Operations Section */}
        <section className="form-section">
          <h3>Fixed Operations Deal Add-ons (Net Sales)</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Parts</label>
              <input
                type="text"
                value={displayValues.fiPartsAmount}
                onChange={(e) => handleChange('fiPartsAmount', e.target.value)}
                placeholder="$0"
              />
            </div>
            <div className="form-group">
              <label>Service</label>
              <input
                type="text"
                value={displayValues.fiServiceAmount}
                onChange={(e) => handleChange('fiServiceAmount', e.target.value)}
                placeholder="$0"
              />
            </div>
            <div className="form-group">
              <label>Apparel & Licensing</label>
              <input
                type="text"
                value={displayValues.fiApparelAmount}
                onChange={(e) => handleChange('fiApparelAmount', e.target.value)}
                placeholder="$0"
              />
            </div>
          </div>
        </section>

        {/* Status Section */}
        <section className="form-section">
          <h3>Status</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Closed/Committed</label>
              <select
                value={formData.closed ? 'yes' : 'no'}
                onChange={(e) => {
                  if (e.target.value === 'yes') {
                    const missing = getMissingFIFields(formData);
                    if (missing.length > 0) {
                      setAlertModal({
                        isOpen: true,
                        title: 'Enter F&I values before closing',
                        message: `Enter a value (use $0 if none) for: ${missing.map(([, label]) => label).join(', ')}`
                      });
                      return;
                    }
                    // Abnormal (but not impossible) gross → confirm before committing.
                    const { warnings } = getAmountIssues(formData);
                    if (warnings.length > 0) {
                      setConfirmModal({
                        isOpen: true,
                        title: 'Does this look right?',
                        message: `${warnings.join(' ')}\n\nClose this deal anyway?`,
                        onConfirm: () => handleChange('closed', true)
                      });
                      return;
                    }
                  }
                  handleChange('closed', e.target.value === 'yes');
                }}
              >
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </select>
            </div>
            <div className="form-group">
              <label>SWR'd</label>
              <select
                value={formData.swrd ? 'yes' : 'no'}
                onChange={(e) => handleChange('swrd', e.target.value === 'yes')}
              >
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </select>
            </div>
          </div>
        </section>

        <div className="form-actions">
          <button type="button" onClick={onClose} className="btn-cancel">
            Cancel
          </button>
          <button type="submit" className="btn-save" disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save & Close'}
          </button>
        </div>
      </form>
      <ConfirmModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        onConfirm={() => setAlertModal({ ...alertModal, isOpen: false })}
        title={alertModal.title}
        message={alertModal.message}
        confirmStyle="primary"
        hideCancel={true}
        confirmText="OK"
      />
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={() => { if (confirmModal.onConfirm) confirmModal.onConfirm(); }}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmStyle="danger"
        confirmText="Close deal anyway"
        cancelText="Go back"
      />
    </div>
  );
}

export default SalesActivity;
